import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { MembershipTier, UserProfileData, UserAchievement } from '../types/membership';
import { saveUserAchievementLocal } from '../utils/indexedDB';

// Vite will replace these with the actual values during build
declare const __SUPABASE_URL__: string;
declare const __SUPABASE_KEY__: string;

// Get Supabase credentials from Vite-injected globals
const supabaseUrl = typeof __SUPABASE_URL__ !== 'undefined' ? __SUPABASE_URL__ : undefined;
const supabaseKey = typeof __SUPABASE_KEY__ !== 'undefined' ? __SUPABASE_KEY__ : undefined;

// Export a flag to check if auth is actually usable
export const isSupabaseConfigured = !!supabaseUrl && !!supabaseKey && supabaseUrl !== 'https://placeholder.supabase.co';

if (!isSupabaseConfigured) {
  console.warn("Supabase credentials missing or invalid. Authentication features will be disabled.");
}

// Provide fallback values to satisfy createClient requirements
// but prevents the app from crashing immediately on import.
export const supabase: SupabaseClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseKey || 'placeholder'
);

// ============================================
// Profile Helper Functions
// ============================================

/**
 * Check if user is Ultra tier (allowed to sync to Supabase)
 */
async function isUltraUser(userId: string): Promise<boolean> {
  if (!userId) {
    return false;
  }
  const profile = await getUserProfile(userId);
  return profile?.membership_tier === 'ultra';
}

/**
 * Fetch user's profile data from the database
 */
export async function getUserProfile(userId: string): Promise<UserProfileData | null> {
  if (!userId) {
    return null;
  }
  
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  
  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
  return data as UserProfileData | null;
}

/**
 * Update user profile fields (only syncs for Ultra tier)
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Pick<UserProfileData,
    | 'membership_tier'
    | 'tier_expires_at'
    | 'total_xp'
    | 'current_level'
    | 'lifetime_xp'
    | 'current_streak'
    | 'longest_streak'
    | 'last_active_date'
    | 'freezes_available'
    | 'freezes_used'
    | 'books_completed'
    | 'streak_multiplier'
    | 'challenges_completed'
    | 'daily_challenge_streak'
  >>
): Promise<boolean> {
  if (!userId) {
    return false;
  }
  // Only sync to Supabase if user is Ultra tier
  if (!await isUltraUser(userId)) {
    return false;
  }

  const { error } = await supabase
    .from('user_profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', userId);
  
  if (error) {
    console.error('Error updating user profile:', error);
    return false;
  }
  return true;
}

/**
 * Get user's current tier with expiry check
 */
export async function getUserTier(userId: string): Promise<{
  tier: MembershipTier;
  isExpired: boolean;
  expiresAt: string | null;
}> {
  if (!userId) {
    return { tier: 'guest', isExpired: false, expiresAt: null };
  }
  const profile = await getUserProfile(userId);
  
  if (!profile) {
    return { tier: 'guest', isExpired: false, expiresAt: null };
  }
  
  const isExpired = profile.tier_expires_at 
    ? new Date(profile.tier_expires_at) < new Date()
    : false;
  
  // Check if tier has expired
  if (isExpired && profile.membership_tier !== 'guest') {
    // Downgrade to guest (free tier) when expiration date passes
    await updateUserProfile(userId, { membership_tier: 'guest' });
    return { tier: 'guest', isExpired: true, expiresAt: profile.tier_expires_at };
  }
  
  return {
    tier: profile.membership_tier,
    isExpired: false,
    expiresAt: profile.tier_expires_at,
  };
}

/**
 * Fetch user tier from Supabase without modifying anything
 * Used during login to sync tier to local state
 */
export async function fetchUserTierFromSupabase(userId: string): Promise<{
  tier: MembershipTier;
  expiresAt: string | null;
  isExpired: boolean;
}> {
  if (!userId) {
    return { tier: 'guest', expiresAt: null, isExpired: false };
  }
  
  const profile = await getUserProfile(userId);
  
  if (!profile) {
    return { tier: 'guest', expiresAt: null, isExpired: false };
  }
  
  const isExpired = profile.tier_expires_at 
    ? new Date(profile.tier_expires_at) < new Date()
    : false;
  
  return {
    tier: profile.membership_tier,
    expiresAt: profile.tier_expires_at,
    isExpired,
  };
}

/**
 * Add XP to user's total (only syncs for Ultra tier)
 */
export async function addUserXP(userId: string, xpAmount: number): Promise<{
  success: boolean;
  newTotal: number;
  newLevel: number;
  leveledUp: boolean;
}> {
  if (!userId) {
    return { success: false, newTotal: 0, newLevel: 1, leveledUp: false };
  }
  // Only sync for Ultra tier
  if (!await isUltraUser(userId)) {
    return { success: false, newTotal: 0, newLevel: 1, leveledUp: false };
  }

  const profile = await getUserProfile(userId);
  
  if (!profile) {
    return { success: false, newTotal: 0, newLevel: 1, leveledUp: false };
  }
  
  const newTotal = profile.total_xp + xpAmount;
  const newLifetime = profile.lifetime_xp + xpAmount;
  
  // Calculate new level (every 500 XP)
  const newLevel = Math.floor(newTotal / 500) + 1;
  const leveledUp = newLevel > profile.current_level;
  
  const success = await updateUserProfile(userId, {
    total_xp: newTotal,
    lifetime_xp: newLifetime,
    current_level: newLevel,
  });
  
  return {
    success,
    newTotal,
    newLevel,
    leveledUp,
  };
}

/**
 * Update user's streak (only syncs for Ultra tier)
 */
export async function updateUserStreak(userId: string): Promise<{
  currentStreak: number;
  longestStreak: number;
  streakBroken: boolean;
}> {
  if (!userId) {
    return { currentStreak: 0, longestStreak: 0, streakBroken: false };
  }
  // Only sync for Ultra tier
  if (!await isUltraUser(userId)) {
    return { currentStreak: 0, longestStreak: 0, streakBroken: false };
  }

  const profile = await getUserProfile(userId);
  
  if (!profile) {
    return { currentStreak: 0, longestStreak: 0, streakBroken: false };
  }
  
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  
  let newStreak = profile.current_streak;
  let streakBroken = false;
  
  if (profile.last_active_date === today) {
    // Already active today, no change
    return { currentStreak: profile.current_streak, longestStreak: profile.longest_streak, streakBroken: false };
  }
  
  if (profile.last_active_date === yesterday) {
    // Continue streak
    newStreak = profile.current_streak + 1;
  } else if (profile.last_active_date === null || profile.last_active_date !== today) {
    // Start new streak (or broken streak)
    if (profile.last_active_date && profile.last_active_date !== today) {
      streakBroken = true;
    }
    newStreak = 1;
  }
  
  const newLongest = Math.max(newStreak, profile.longest_streak);
  
  await updateUserProfile(userId, {
    current_streak: newStreak,
    longest_streak: newLongest,
    last_active_date: today,
  });
  
  return { currentStreak: newStreak, longestStreak: newLongest, streakBroken };
}

/**
 * Get user's unlocked achievements
 */
export async function getUserAchievements(userId: string): Promise<UserAchievement[]> {
  if (!userId) {
    return [];
  }
  const { data, error } = await supabase
    .from('user_achievements')
    .select('*')
    .eq('user_id', userId)
    .order('unlocked_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching user achievements:', error);
    return [];
  }
  return data as UserAchievement[];
}

/**
 * Unlock an achievement for a user
 * - ALWAYS saves to local IndexedDB (all tiers: Guest, Starter, Reader, Ultra)
 * - Also syncs to Supabase ONLY for Ultra tier
 */
export async function unlockAchievement(
  userId: string,
  achievementId: string,
  xpReward: number
): Promise<{ success: boolean; isNew: boolean }> {
  if (!userId) {
    return { success: false, isNew: false };
  }
  // 1. Always save locally first (regardless of tier)
  try {
    await saveUserAchievementLocal({
      userId,
      achievementId,
      unlockedAt: new Date().toISOString(),
      xpEarned: xpReward,
    });
  } catch (error) {
    console.error('Failed to save achievement locally:', error);
    // Continue anyway - local save failure shouldn't block achievement
  }

  // 2. Only sync to Supabase if user is Ultra
  if (!await isUltraUser(userId)) {
    return { success: true, isNew: true }; // Local save succeeded
  }

  // 3. Ultra path: check if already unlocked remotely
  const { data: existing } = await supabase
    .from('user_achievements')
    .select('id')
    .eq('user_id', userId)
    .eq('achievement_id', achievementId)
    .single();
  
  if (existing) {
    return { success: true, isNew: false };
  }
  
  // 4. Insert new achievement remotely
  const { error } = await supabase
    .from('user_achievements')
    .insert({
      user_id: userId,
      achievement_id: achievementId,
      xp_earned: xpReward,
    });
  
  if (error) {
    console.error('Error unlocking achievement:', error);
    return { success: false, isNew: false };
  }
  
  // 5. Award XP for the achievement (Ultra only)
  if (xpReward > 0) {
    await addUserXP(userId, xpReward);
  }
  
  return { success: true, isNew: true };
}
